import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ArrowRight,
  MapPin,
  Mail,
  Phone,
  MessageSquare,
  Linkedin,
  Instagram,
  Twitter,
  Youtube,
  CheckCircle2,
  Sparkles,
} from 'lucide-react';
import SEO from '../SEO';
import { CONTACT } from '../../mock';
import { portalApi } from '../../lib/portalApi';

const IDENTIFY_ROLES = [
  'Founder',
  'Investor',
  'Business / Corporate',
  'Enabler / Ecosystem Partner',
  'Mentor / Expert',
  'Explorer',
];

const CONNECT_INTERESTS = [
  'Venture Studio: InnovVentures',
  'Accelerator: InnovACE',
  'Brand & Growth: InnovIdea',
  'Technology & AI: InnovVeda.ai',
  'Studio I',
  'iSSi',
  'InnovZ',
  'Something Else',
];

const INTENT_TO_INTEREST = {
  build: 'Venture Studio: InnovVentures',
  partner: 'Something Else',
  innovace: 'Accelerator: InnovACE',
  studio: 'Studio I',
  issi: 'iSSi',
  innovz: 'InnovZ',
};

export default function ContactPage() {
  const [searchParams] = useSearchParams();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [linkedin, setLinkedin] = useState('');
  const [role, setRole] = useState('');
  const [interests, setInterests] = useState([]);
  const [message, setMessage] = useState('');

  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    const intent = (searchParams.get('intent') || '').toLowerCase();
    const mapped = INTENT_TO_INTEREST[intent];
    if (mapped) setInterests([mapped]);
  }, [searchParams]);

  const toggleInterest = (item) => {
    setInterests((prev) =>
      prev.includes(item) ? prev.filter((i) => i !== item) : [...prev, item]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !phone || !role) {
      setError('Please fill in all required fields (Name, Email, Phone/WhatsApp, and your Role).');
      return;
    }
    setError('');
    setLoading(true);

    try {
      const formattedMessage = [
        `Role: ${role}`,
        `Connect for: ${interests.join(', ') || 'General'}`,
        linkedin ? `LinkedIn: ${linkedin}` : null,
        message ? `Notes: ${message}` : null,
      ]
        .filter(Boolean)
        .join('\n\n');

      await portalApi.submitContact({
        name,
        email,
        phone,
        role,
        interests,
        linkedin,
        enquiryType: interests[0] || 'General Inquiry',
        message: formattedMessage,
      });

      setSent(true);
      setName('');
      setEmail('');
      setPhone('');
      setLinkedin('');
      setMessage('');
      setRole('');
      setInterests([]);
    } catch (err) {
      setError(err.message || 'Could not send your message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#08080a] text-white min-h-screen selection:bg-[#d42b92]/30">
      <SEO
        title="Contact Us"
        description="Start a conversation with InnovHer. Whether you are building a venture, exploring the next opportunity, bringing expertise, or curious about the ecosystem."
        keywords="contact InnovHer, venture studio Jaipur, InnovACE, Studio-I, build with InnovHer"
        path="/contact"
      />

      {/* Ambient pink glow */}
      <div className="absolute top-20 right-1/4 w-[600px] h-[600px] rounded-full bg-[#d42b92]/10 blur-[180px] pointer-events-none" />
      <div className="absolute top-[600px] left-10 w-[500px] h-[500px] rounded-full bg-[#d42b92]/8 blur-[160px] pointer-events-none" />

      {/* Header Section */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            {/* Eyebrow */}
            {/* <div className="text-[#ff8ec5] text-xs font-semibold tracking-[0.3em] uppercase mb-4 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#d42b92] inline-block animate-pulse" />
              <span>START A CONVERSATION</span>
            </div> */}

            {/* Headline */}
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-white leading-[1.08]">
              Every meaningful connection{' '}
              <span className="text-[#d42b92]">can be a beginning.</span>
            </h1>

            {/* Narrative Paragraphs */}
            <div className="mt-8 space-y-5 text-white/80 text-base sm:text-lg leading-relaxed font-normal">
              <p>
                Whether you are building a venture, looking for the next opportunity, bringing expertise to the ecosystem or simply curious about what InnovHer is creating, there is a place to start a conversation.
              </p>
              <p className="text-white/90 font-medium">
                Tell us a little about yourself and what brings you here. The InnovHer team will connect you with the most relevant part of the ecosystem.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content: Form + Contact Info Sidebar */}
      <section className="relative py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            {/* Left Column: The Form */}
            <div className="lg:col-span-7">
              <div className="rounded-3xl border border-white/10 bg-[#0d0d12]/90 backdrop-blur-md p-6 sm:p-10 shadow-2xl relative overflow-hidden">
                {/* Subtle border glow accent */}
                <div className="absolute top-0 right-0 w-40 h-40 bg-[#d42b92]/10 blur-3xl pointer-events-none" />

                {sent ? (
                  <div className="py-12 text-center space-y-4">
                    <div className="w-16 h-16 rounded-full bg-[#d42b92]/20 border border-[#d42b92] flex items-center justify-center mx-auto text-[#ff8ec5]">
                      <CheckCircle2 className="w-8 h-8" />
                    </div>
                    <h3 className="text-2xl font-bold text-white">Conversation Started!</h3>
                    <p className="text-white/70 max-w-md mx-auto text-sm leading-relaxed">
                      Thank you for reaching out. The InnovHer team has received your details and will connect you with the relevant part of the ecosystem shortly.
                    </p>
                    <button
                      type="button"
                      onClick={() => setSent(false)}
                      className="mt-6 px-6 py-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-semibold tracking-wider transition-colors"
                    >
                      Send another message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-8">
                    {/* Basic Info Fields */}
                    <div className="space-y-5">
                      <div>
                        <label className="block text-xs uppercase tracking-wider text-white/60 font-medium mb-2">
                          Name <span className="text-[#ff8ec5]">*</span>
                        </label>
                        <input
                          type="text"
                          required
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="Your full name"
                          className="w-full bg-white/[0.03] border border-white/10 focus:border-[#d42b92] focus:bg-white/[0.05] rounded-xl px-4 py-3 text-white text-sm outline-none transition-all placeholder:text-white/25"
                        />
                      </div>

                      <div className="grid sm:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-xs uppercase tracking-wider text-white/60 font-medium mb-2">
                            Email <span className="text-[#ff8ec5]">*</span>
                          </label>
                          <input
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="you@domain.com"
                            className="w-full bg-white/[0.03] border border-white/10 focus:border-[#d42b92] focus:bg-white/[0.05] rounded-xl px-4 py-3 text-white text-sm outline-none transition-all placeholder:text-white/25"
                          />
                        </div>

                        <div>
                          <label className="block text-xs uppercase tracking-wider text-white/60 font-medium mb-2">
                            Phone / WhatsApp <span className="text-[#ff8ec5]">*</span>
                          </label>
                          <input
                            type="tel"
                            required
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            placeholder="+91 98765 43210"
                            className="w-full bg-white/[0.03] border border-white/10 focus:border-[#d42b92] focus:bg-white/[0.05] rounded-xl px-4 py-3 text-white text-sm outline-none transition-all placeholder:text-white/25"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs uppercase tracking-wider text-white/60 font-medium mb-2">
                          LinkedIn Profile <span className="text-white/40 text-[11px] font-normal">(Optional)</span>
                        </label>
                        <input
                          type="url"
                          value={linkedin}
                          onChange={(e) => setLinkedin(e.target.value)}
                          placeholder="https://linkedin.com/in/yourprofile"
                          className="w-full bg-white/[0.03] border border-white/10 focus:border-[#d42b92] focus:bg-white/[0.05] rounded-xl px-4 py-3 text-white text-sm outline-none transition-all placeholder:text-white/25"
                        />
                      </div>
                    </div>

                    {/* I Identify As (Single Select / Radio) */}
                    <div className="pt-4 border-t border-white/10">
                      <label className="block text-xs uppercase tracking-wider text-white/70 font-semibold mb-3">
                        I identify as <span className="text-[#ff8ec5]">*</span>
                      </label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                        {IDENTIFY_ROLES.map((r) => {
                          const isSelected = role === r;
                          return (
                            <button
                              key={r}
                              type="button"
                              onClick={() => setRole(r)}
                              className={`flex items-center gap-2.5 px-3.5 py-3 rounded-xl border text-left text-xs sm:text-[13px] font-medium transition-all ${isSelected
                                  ? 'border-[#d42b92] bg-[#d42b92]/20 text-white shadow-md shadow-[#d42b92]/10'
                                  : 'border-white/10 bg-white/[0.02] text-white/70 hover:border-white/20 hover:text-white'
                                }`}
                            >
                              <span
                                className={`w-3.5 h-3.5 rounded-full border flex items-center justify-center shrink-0 ${isSelected
                                    ? 'border-[#ff8ec5] bg-[#d42b92]'
                                    : 'border-white/30 bg-transparent'
                                  }`}
                              >
                                {isSelected && (
                                  <span className="w-1.5 h-1.5 rounded-full bg-white" />
                                )}
                              </span>
                              <span className="truncate">{r}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* I'd like to connect for (Multi-Select / Checkbox) */}
                    <div className="pt-4 border-t border-white/10">
                      <label className="block text-xs uppercase tracking-wider text-white/70 font-semibold mb-3">
                        I’d like to connect for
                      </label>
                      <div className="grid sm:grid-cols-2 gap-2.5">
                        {CONNECT_INTERESTS.map((item) => {
                          const isChecked = interests.includes(item);
                          return (
                            <button
                              key={item}
                              type="button"
                              onClick={() => toggleInterest(item)}
                              className={`flex items-center gap-3 px-3.5 py-3 rounded-xl border text-left text-xs sm:text-[13px] font-medium transition-all ${isChecked
                                  ? 'border-[#d42b92] bg-[#d42b92]/15 text-white shadow-sm'
                                  : 'border-white/10 bg-white/[0.02] text-white/70 hover:border-white/20 hover:text-white'
                                }`}
                            >
                              <span
                                className={`w-4 h-4 rounded-md border flex items-center justify-center shrink-0 transition-colors ${isChecked
                                    ? 'border-[#d42b92] bg-[#d42b92] text-white'
                                    : 'border-white/30 bg-transparent'
                                  }`}
                              >
                                {isChecked && <span className="text-[10px] font-bold">✓</span>}
                              </span>
                              <span className="leading-tight">{item}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Optional Message Field */}
                    <div className="pt-4 border-t border-white/10">
                      <label className="block text-xs uppercase tracking-wider text-white/60 font-medium mb-2">
                        Message / Notes <span className="text-white/40 text-[11px] font-normal">(Optional)</span>
                      </label>
                      <textarea
                        rows={3}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Tell us a little more about your venture, idea, or how we might collaborate..."
                        className="w-full bg-white/[0.03] border border-white/10 focus:border-[#d42b92] focus:bg-white/[0.05] rounded-xl px-4 py-3 text-white text-sm outline-none transition-all placeholder:text-white/25 resize-none"
                      />
                    </div>

                    {error && (
                      <p className="text-xs sm:text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-xl p-3">
                        {error}
                      </p>
                    )}

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-4 px-6 rounded-2xl bg-[#d42b92] hover:bg-[#e0339f] disabled:opacity-60 text-white font-bold text-sm sm:text-base tracking-wider transition-all duration-300 flex items-center justify-center gap-3 shadow-lg shadow-[#d42b92]/25 group"
                    >
                      <span>{loading ? 'SENDING...' : 'START A CONVERSATION'}</span>
                      <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                    </button>
                  </form>
                )}
              </div>
            </div>

            {/* Right Column: CONTACT INNOVHER Information */}
            <div className="lg:col-span-5 space-y-8">
              <div className="rounded-3xl border border-white/10 bg-[#0d0d12]/70 backdrop-blur-md p-8 sm:p-10 space-y-8">
                {/* Header */}
                <div>
                  <div className="text-[#ff8ec5] text-xs font-semibold tracking-[0.25em] uppercase mb-2">
                    REACH OUT
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
                    CONTACT INNOVHER
                  </h2>
                </div>

                {/* Offices */}
                <div className="space-y-6 pt-4 border-t border-white/10">
                  <div>
                    <h4 className="text-xs uppercase tracking-wider text-white/50 font-semibold mb-1 flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5 text-[#ff8ec5]" />
                      <span>Corporate Office</span>
                    </h4>
                    <p className="text-sm sm:text-base text-white/90 font-medium">
                      Studio I, Innovation Corridors, Jaipur, Rajasthan · 302022, India
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xs uppercase tracking-wider text-white/50 font-semibold mb-1 flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5 text-[#ff8ec5]" />
                      <span>Registered Office</span>
                    </h4>
                    <p className="text-sm sm:text-base text-white/90 font-medium">
                      Jaipur, Rajasthan · India
                    </p>
                  </div>
                </div>

                {/* Direct Connect Options */}
                <div className="space-y-4 pt-6 border-t border-white/10">
                  <div className="text-xs uppercase tracking-wider text-white/50 font-semibold mb-2">
                    Connect
                  </div>
                  <div className="flex flex-col sm:flex-row flex-wrap gap-4 text-sm text-white/80">
                    <a
                      href={`mailto:${CONTACT.email}`}
                      className="inline-flex items-center gap-2 hover:text-[#ff8ec5] transition-colors"
                    >
                      <Mail className="w-4 h-4 text-[#ff8ec5]" />
                      <span>{CONTACT.email}</span>
                    </a>
                    <span className="hidden sm:inline text-white/20">|</span>
                    <a
                      href={`tel:${CONTACT.phone.replace(/\s+/g, '')}`}
                      className="inline-flex items-center gap-2 hover:text-[#ff8ec5] transition-colors"
                    >
                      <Phone className="w-4 h-4 text-[#ff8ec5]" />
                      <span>{CONTACT.phone}</span>
                    </a>
                    <span className="hidden sm:inline text-white/20">|</span>
                    <a
                      href={`https://wa.me/${CONTACT.whatsapp.replace(/[^0-9]/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-[#25D366] hover:underline transition-colors"
                    >
                      <MessageSquare className="w-4 h-4" />
                      <span>WhatsApp</span>
                    </a>
                  </div>
                </div>

                {/* Social Media Links */}
                <div className="pt-6 border-t border-white/10">
                  <div className="text-xs uppercase tracking-wider text-white/50 font-semibold mb-3">
                    Follow the journey
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <a
                      href={CONTACT.socials.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-white/[0.04] hover:bg-[#0077b5] border border-white/10 text-white text-xs font-medium transition-all"
                    >
                      <Linkedin className="w-3.5 h-3.5" />
                      <span>LinkedIn</span>
                    </a>
                    <a
                      href={CONTACT.socials.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-white/[0.04] hover:bg-[#E1306C] border border-white/10 text-white text-xs font-medium transition-all"
                    >
                      <Instagram className="w-3.5 h-3.5" />
                      <span>Instagram</span>
                    </a>
                    <a
                      href={CONTACT.socials.twitter}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-white/[0.04] hover:bg-white hover:text-black border border-white/10 text-white text-xs font-medium transition-all"
                    >
                      <Twitter className="w-3.5 h-3.5" />
                      <span>X</span>
                    </a>
                    <a
                      href={CONTACT.socials.youtube}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-white/[0.04] hover:bg-[#FF0000] border border-white/10 text-white text-xs font-medium transition-all"
                    >
                      <Youtube className="w-3.5 h-3.5" />
                      <span>YouTube</span>
                    </a>
                  </div>
                </div>

                {/* Brand Signature */}
                <div className="pt-6 border-t border-white/10">
                  <div className="text-lg font-bold text-white tracking-wider flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-[#d42b92]" />
                    <span>InnovHer</span>
                  </div>
                  <p className="mt-1 text-xs text-white/50 italic tracking-wide">
                    Where ambition becomes enterprise.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
